import * as React from 'react'
import { AlertTriangle, Loader2, Play } from 'lucide-react'
import {
  useActiveBranchRecord,
  useRunCypherWriteMutation,
  type CypherWriteCounters,
  type CypherWriteResult
} from '@axilon-apps/domains-ontology-ui-state'

import { useProtectedBranchGuard } from '../../components/Branches/ProtectedBranchPromptDialog'
import { Button } from '../../components/ui/button'
import { Card, CardContent } from '../../components/ui/card'
import { Label } from '../../components/ui/label'
import { Textarea } from '../../components/ui/textarea'
import { ConfirmDialog } from '../ConfigurePage/components/DeleteConfirmDialog'
import { splitCypherStatements } from './cypher-write-split'

const QUERY_STORAGE_KEY = 'ontology.settings.cypherWrite.query'
const PARAMS_STORAGE_KEY = 'ontology.settings.cypherWrite.params'
const QUERY_PERSIST_DEBOUNCE_MS = 500

interface QueryRunOutcome {
  query: string
  result?: CypherWriteResult
  error?: string
}

function loadStored(key: string): string {
  try {
    return window.localStorage.getItem(key) ?? ''
  } catch {
    return ''
  }
}

function persistStored(key: string, value: string): void {
  try {
    if (value.length === 0) window.localStorage.removeItem(key)
    else window.localStorage.setItem(key, value)
  } catch {
    // best-effort
  }
}

function parseParamsJson(raw: string): Record<string, unknown> | string {
  const trimmed = raw.trim()
  if (trimmed.length === 0) return {}
  try {
    const parsed: unknown = JSON.parse(trimmed)
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return 'Params must be a JSON object.'
    }
    return parsed as Record<string, unknown>
  } catch {
    return 'Params must be valid JSON.'
  }
}

function getErrorMessage(error: unknown): string {
  if (!error || typeof error !== 'object') return 'Unknown error.'
  const obj = error as Record<string, unknown>
  if (obj['data'] && typeof obj['data'] === 'object') {
    const data = obj['data'] as Record<string, unknown>
    if (typeof data['message'] === 'string') return data['message']
    if (typeof data['error'] === 'string') return data['error']
  }
  if (typeof obj['error'] === 'string') return obj['error']
  if (typeof obj['message'] === 'string') return obj['message']
  return 'Unknown error.'
}

function formatCounters(counters: CypherWriteCounters): string {
  const parts: string[] = []
  if (counters.nodesCreated) parts.push(`${counters.nodesCreated} nodes created`)
  if (counters.nodesDeleted) parts.push(`${counters.nodesDeleted} nodes deleted`)
  if (counters.relationshipsCreated) {
    parts.push(`${counters.relationshipsCreated} relationships created`)
  }
  if (counters.relationshipsDeleted) {
    parts.push(`${counters.relationshipsDeleted} relationships deleted`)
  }
  if (counters.propertiesSet) parts.push(`${counters.propertiesSet} properties set`)
  if (counters.labelsAdded) parts.push(`${counters.labelsAdded} labels added`)
  if (counters.labelsRemoved) parts.push(`${counters.labelsRemoved} labels removed`)
  return parts.length > 0 ? parts.join(' · ') : 'No graph counters reported.'
}

function safeStringify(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2)
  } catch {
    return String(value)
  }
}

export function CypherWriteTab({ embedded = false }: { embedded?: boolean }) {
  const activeBranch = useActiveBranchRecord()
  const branchLabel = activeBranch?.name?.trim() || 'main'
  const branchGuard = useProtectedBranchGuard(activeBranch)

  const [query, setQuery] = React.useState(() => loadStored(QUERY_STORAGE_KEY))
  const [paramsText, setParamsText] = React.useState(() => loadStored(PARAMS_STORAGE_KEY))
  const [paramsError, setParamsError] = React.useState('')
  const [confirmOpen, setConfirmOpen] = React.useState(false)
  const [outcomes, setOutcomes] = React.useState<QueryRunOutcome[]>([])
  const [runProgress, setRunProgress] = React.useState<{ current: number; total: number } | null>(
    null
  )

  const [runCypherWrite, writeState] = useRunCypherWriteMutation()

  const statements = React.useMemo(() => splitCypherStatements(query), [query])

  React.useEffect(() => {
    const id = window.setTimeout(
      () => persistStored(QUERY_STORAGE_KEY, query),
      QUERY_PERSIST_DEBOUNCE_MS
    )
    return () => window.clearTimeout(id)
  }, [query])

  React.useEffect(() => {
    const id = window.setTimeout(
      () => persistStored(PARAMS_STORAGE_KEY, paramsText),
      QUERY_PERSIST_DEBOUNCE_MS
    )
    return () => window.clearTimeout(id)
  }, [paramsText])

  const executeWrite = React.useCallback(async () => {
    const parsedParams = parseParamsJson(paramsText)
    if (typeof parsedParams === 'string') {
      setParamsError(parsedParams)
      return
    }
    const queries = splitCypherStatements(query)
    if (queries.length === 0) return

    setParamsError('')
    setOutcomes([])
    setRunProgress({ current: 0, total: queries.length })

    const nextOutcomes: QueryRunOutcome[] = []
    try {
      for (let i = 0; i < queries.length; i++) {
        setRunProgress({ current: i + 1, total: queries.length })
        try {
          const result = await runCypherWrite({ query: queries[i]!, params: parsedParams }).unwrap()
          nextOutcomes.push({ query: queries[i]!, result })
          setOutcomes([...nextOutcomes])
        } catch (err) {
          nextOutcomes.push({ query: queries[i]!, error: getErrorMessage(err) })
          setOutcomes([...nextOutcomes])
          break
        }
      }
    } finally {
      setRunProgress(null)
      setConfirmOpen(false)
    }
  }, [paramsText, query, runCypherWrite])

  const handleRunClick = React.useCallback(() => {
    if (statements.length === 0) return
    if (!branchGuard.ensureWritable()) return
    const parsedParams = parseParamsJson(paramsText)
    if (typeof parsedParams === 'string') {
      setParamsError(parsedParams)
      return
    }
    setParamsError('')
    setConfirmOpen(true)
  }, [branchGuard, paramsText, statements.length])

  const isRunning = writeState.isLoading || runProgress !== null
  const canRun = statements.length > 0 && !isRunning

  const confirmDescription =
    statements.length === 1
      ? `This will run 1 query on branch "${branchLabel}" and mutate graph data immediately.`
      : `This will run ${statements.length} queries sequentially on branch "${branchLabel}". Execution stops on the first failure.`

  return (
    <div className="flex flex-col gap-card">
      <Card className="gap-3 py-4">
        <CardContent className="flex flex-col gap-4">
          {embedded ? null : (
            <div className="flex flex-col gap-1">
              <h3 className="text-sm font-semibold text-ink">Cypher write console</h3>
              <p className="text-xs text-ink-3">
                Submit arbitrary write queries against the active ontology branch. Separate multiple
                statements with semicolons.
              </p>
            </div>
          )}

          <div
            role="alert"
            className="flex items-start gap-2 rounded-md border border-st-warn/30 bg-st-warn-bg px-3 py-2 text-xs text-st-warn"
          >
            <AlertTriangle className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
            <span>
              Writes execute immediately on branch{' '}
              <span className="font-mono font-medium">{branchLabel}</span>. There is no undo.
            </span>
          </div>

          <div className="flex flex-col gap-2">
            <div className="flex items-baseline justify-between gap-2">
              <Label htmlFor="settings-cypher-write-query" className="text-xs text-ink-2">
                Cypher queries
              </Label>
              {statements.length > 0 ? (
                <span className="text-11 text-ink-4">
                  {statements.length} {statements.length === 1 ? 'statement' : 'statements'}
                </span>
              ) : null}
            </div>
            <Textarea
              id="settings-cypher-write-query"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => {
                if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
                  event.preventDefault()
                  handleRunClick()
                }
              }}
              rows={12}
              spellCheck={false}
              placeholder={`MERGE (n:Example {id: $id}) SET n.name = $name RETURN n;\nMATCH (m:Example {id: $id}) SET m.updated = true`}
              className="font-mono text-13"
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="settings-cypher-write-params" className="text-xs text-ink-2">
              Parameters (optional JSON object, shared by all statements)
            </Label>
            <Textarea
              id="settings-cypher-write-params"
              value={paramsText}
              onChange={(event) => {
                setParamsText(event.target.value)
                if (paramsError) setParamsError('')
              }}
              rows={4}
              spellCheck={false}
              placeholder='{ "id": "urn:axilon:example:1", "name": "Example" }'
              className="font-mono text-13"
              aria-invalid={paramsError.length > 0}
            />
            {paramsError ? (
              <span className="text-xs text-st-down">{paramsError}</span>
            ) : (
              <span className="text-11 text-ink-4">
                Referenced in queries as <code className="font-mono">$paramName</code>.
              </span>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Button
              type="button"
              variant="outline"
              className="border-st-warn/40 bg-st-warn-bg text-st-warn hover:bg-st-warn-bg hover:text-st-warn"
              onClick={handleRunClick}
              disabled={!canRun}
            >
              {isRunning ? <Loader2 className="animate-spin" /> : <Play className="fill-current" />}
              {isRunning && runProgress
                ? `Running ${runProgress.current}/${runProgress.total}…`
                : isRunning
                  ? 'Running…'
                  : statements.length > 1
                    ? `Execute ${statements.length} writes`
                    : 'Execute write'}
            </Button>
            <span className="text-11 text-ink-4">⌘↵ to run</span>
          </div>
        </CardContent>
      </Card>

      <WriteResultsCard outcomes={outcomes} />

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={statements.length > 1 ? 'Execute Cypher writes?' : 'Execute Cypher write?'}
        description={confirmDescription}
        confirmLabel={statements.length > 1 ? 'Execute writes' : 'Execute write'}
        destructive
        busy={isRunning}
        onConfirm={() => void executeWrite()}
      />
      {branchGuard.dialog}
    </div>
  )
}

function WriteResultsCard({ outcomes }: { outcomes: QueryRunOutcome[] }) {
  if (outcomes.length === 0) {
    return (
      <Card className="gap-3 py-4">
        <CardContent>
          <p className="text-xs text-ink-4">
            Run write queries to see per-statement counters and returned rows.
          </p>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="gap-3 py-4">
      <CardContent className="flex flex-col gap-4">
        <h3 className="text-sm font-semibold text-ink">
          Results ({outcomes.length} {outcomes.length === 1 ? 'statement' : 'statements'})
        </h3>
        <div className="flex flex-col gap-3">
          {outcomes.map((outcome, index) => (
            <QueryOutcomeBlock key={`${index}:${outcome.query}`} index={index} outcome={outcome} />
          ))}
        </div>
      </CardContent>
    </Card>
  )
}

function QueryOutcomeBlock({ index, outcome }: { index: number; outcome: QueryRunOutcome }) {
  const rows = outcome.result?.results ?? []
  const counters = outcome.result?.counters

  return (
    <div className="flex flex-col gap-2 rounded-md border border-line bg-bg-sunk/40 p-3">
      <div className="flex flex-wrap items-baseline gap-2">
        <span className="text-10 font-semibold uppercase tracking-[0.08em] text-ink-4">
          Statement {index + 1}
        </span>
        {outcome.error ? (
          <span className="text-11 font-medium text-st-down">Failed</span>
        ) : (
          <span className="text-11 font-medium text-st-run">OK</span>
        )}
      </div>
      <pre className="overflow-x-auto whitespace-pre-wrap font-mono text-11 text-ink-3">
        {outcome.query}
      </pre>
      {outcome.error ? (
        <div role="alert" className="rounded-md bg-st-down-bg px-2.5 py-1.5 text-xs text-st-down">
          {outcome.error}
        </div>
      ) : null}
      {!outcome.error && counters ? (
        <p className="text-xs text-ink-2">{formatCounters(counters)}</p>
      ) : null}
      {!outcome.error && !counters ? (
        <p className="text-xs text-ink-3">No write counters returned.</p>
      ) : null}
      {!outcome.error && rows.length > 0 ? (
        <pre className="max-h-64 overflow-auto rounded-md border border-line bg-bg-sunk p-2 font-mono text-11 text-ink-2">
          {safeStringify(rows)}
        </pre>
      ) : null}
      {!outcome.error && rows.length === 0 ? (
        <p className="text-xs text-ink-3">Query returned 0 rows.</p>
      ) : null}
    </div>
  )
}
